import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import {
  create_hvac_label,
  regenerate_missing_hvac_pdfs,
  get_hvac_labels,
  get_hvac_label,
  get_hvac_counts,
  get_hvac_logs,
  get_hvac_areas,
  bulk_send_for_cleaning,
  bulk_mark_cleaned_and_assign,
  bulk_install_from_spare,
  bulk_inactivate_hvac,
  reactivate_hvac,
  generate_hvac_usage_log_report
} from './api';

const initialState = {
  loading: false,
  list: [],
  listPagination: {},
  pendingCount: 0,
  stalePendingCount: 0,
  current: null,
  counts: {
    total: 0,
    inactive: 0,
    in_use: 0,
    in_spare_stock: 0,
    to_be_cleaned: 0
  },
  hvacLogs: [],
  hvacLogsLoading: false,
  areas: [],
  areasLoading: false,
  selectedRowKeys: [],
  activeTab: 'all',
  areaSearch: '',
  bulkActionLoading: false,
  reportGenerating: false
};

const actions = {
  CREATE_HVAC_LABEL: 'hvacFilter/CREATE_HVAC_LABEL',
  REGENERATE_MISSING_HVAC_PDFS: 'hvacFilter/REGENERATE_MISSING_HVAC_PDFS',
  GET_HVAC_LABELS: 'hvacFilter/GET_HVAC_LABELS',
  GET_HVAC_LABEL: 'hvacFilter/GET_HVAC_LABEL',
  GET_HVAC_COUNTS: 'hvacFilter/GET_HVAC_COUNTS',
  GET_HVAC_LOGS: 'hvacFilter/GET_HVAC_LOGS',
  GET_HVAC_AREAS: 'hvacFilter/GET_HVAC_AREAS',
  BULK_SEND_FOR_CLEANING: 'hvacFilter/BULK_SEND_FOR_CLEANING',
  BULK_MARK_CLEANED_AND_ASSIGN: 'hvacFilter/BULK_MARK_CLEANED_AND_ASSIGN',
  BULK_INSTALL_FROM_SPARE: 'hvacFilter/BULK_INSTALL_FROM_SPARE',
  BULK_INACTIVATE: 'hvacFilter/BULK_INACTIVATE',
  REACTIVATE_HVAC: 'hvacFilter/REACTIVATE_HVAC',
  GENERATE_HVAC_USAGE_LOG_REPORT: 'hvacFilter/GENERATE_HVAC_USAGE_LOG_REPORT'
};

export const createHvacLabel = createAsyncThunk(
  actions.CREATE_HVAC_LABEL,
  async (payload) => {
    return await create_hvac_label(payload);
  }
);

export const regenerateMissingHvacPdfs = createAsyncThunk(
  actions.REGENERATE_MISSING_HVAC_PDFS,
  async () => {
    return await regenerate_missing_hvac_pdfs();
  }
);

export const getHvacLabels = createAsyncThunk(
  actions.GET_HVAC_LABELS,
  async (payload) => {
    return await get_hvac_labels(payload);
  }
);

export const getHvacLabel = createAsyncThunk(
  actions.GET_HVAC_LABEL,
  async (id) => {
    return await get_hvac_label(id);
  }
);

export const getHvacCounts = createAsyncThunk(
  actions.GET_HVAC_COUNTS,
  async () => {
    return await get_hvac_counts();
  }
);

export const getHvacLogs = createAsyncThunk(
  actions.GET_HVAC_LOGS,
  async (hvacLabelNumber) => {
    return await get_hvac_logs(hvacLabelNumber);
  }
);

export const getHvacAreas = createAsyncThunk(
  actions.GET_HVAC_AREAS,
  async () => {
    return await get_hvac_areas();
  }
);

export const bulkSendForCleaning = createAsyncThunk(
  actions.BULK_SEND_FOR_CLEANING,
  async (payload, { rejectWithValue }) => {
    const res = await bulk_send_for_cleaning(payload);
    if (!res?.success) return rejectWithValue(res);
    return res;
  }
);

export const bulkMarkCleanedAndAssign = createAsyncThunk(
  actions.BULK_MARK_CLEANED_AND_ASSIGN,
  async (payload, { rejectWithValue }) => {
    const res = await bulk_mark_cleaned_and_assign(payload);
    if (!res?.success) return rejectWithValue(res);
    return res;
  }
);

export const bulkInstallFromSpare = createAsyncThunk(
  actions.BULK_INSTALL_FROM_SPARE,
  async (payload, { rejectWithValue }) => {
    const res = await bulk_install_from_spare(payload);
    if (!res?.success) return rejectWithValue(res);
    return res;
  }
);

export const bulkInactivateHvac = createAsyncThunk(
  actions.BULK_INACTIVATE,
  async (payload, { rejectWithValue }) => {
    const res = await bulk_inactivate_hvac(payload);
    if (!res?.success) return rejectWithValue(res);
    return res;
  }
);

export const reactivateHvac = createAsyncThunk(
  actions.REACTIVATE_HVAC,
  async ({ hvacLabelNumber, note }, { rejectWithValue }) => {
    const res = await reactivate_hvac(hvacLabelNumber, { note });
    if (!res?.success) return rejectWithValue(res);
    return res;
  }
);

export const generateHvacUsageLogReport = createAsyncThunk(
  actions.GENERATE_HVAC_USAGE_LOG_REPORT,
  async (payload, { rejectWithValue }) => {
    const res = await generate_hvac_usage_log_report(payload);
    if (!res?.success) return rejectWithValue(res);
    return res;
  }
);

const handleIneligibleError = (action) => {
  const payload = action.payload;
  const msg = payload?.message || action.error?.message || 'Action failed.';
  if (payload?.data?.ineligible && Array.isArray(payload.data.ineligible)) {
    const lines = payload.data.ineligible
      .slice(0, 5)
      .map((i) => `${i.hvac_label_number}: ${i.reason}`)
      .join('\n');
    message.error(`${msg}\n${lines}`, 8);
  } else {
    message.error(msg);
  }
};

const hvacFilterSlice = createSlice({
  name: 'hvacFilter',
  initialState,
  reducers: {
    setSelectedRowKeys(state, action) {
      state.selectedRowKeys = action.payload || [];
    },
    setActiveTab(state, action) {
      state.activeTab = action.payload || 'all';
    },
    setAreaSearch(state, action) {
      state.areaSearch = action.payload || '';
    },
    clearSelection(state) {
      state.selectedRowKeys = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createHvacLabel.pending, (state) => {
        state.loading = true;
      })
      .addCase(createHvacLabel.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) {
          message.success(msg);
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(createHvacLabel.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(regenerateMissingHvacPdfs.pending, (state) => {
        state.loading = true;
      })
      .addCase(regenerateMissingHvacPdfs.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) {
          message.success(msg);
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(regenerateMissingHvacPdfs.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(getHvacLabels.pending, (state) => {
        state.loading = true;
      })
      .addCase(getHvacLabels.fulfilled, (state, action) => {
        state.loading = false;
        const {
          success,
          data,
          pagination,
          pending_count,
          stale_pending_count,
          message: msg
        } = action.payload || {};
        if (success) {
          state.list = data || [];
          state.listPagination = pagination || {};
          state.pendingCount = pending_count || 0;
          state.stalePendingCount = stale_pending_count || 0;
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(getHvacLabels.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(getHvacLabel.pending, (state) => {
        state.loading = true;
      })
      .addCase(getHvacLabel.fulfilled, (state, action) => {
        state.loading = false;
        const { success, data, message: msg } = action.payload || {};
        if (success) {
          state.current = data;
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(getHvacLabel.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(getHvacCounts.fulfilled, (state, action) => {
        const { success, data } = action.payload || {};
        if (success && data) {
          state.counts = {
            total: data.total || 0,
            inactive: data.inactive || 0,
            in_use: data.in_use || 0,
            in_spare_stock: data.in_spare_stock || 0,
            to_be_cleaned: data.to_be_cleaned || 0
          };
        }
      })
      .addCase(getHvacCounts.rejected, () => {});

    builder
      .addCase(getHvacLogs.pending, (state) => {
        state.hvacLogsLoading = true;
        state.hvacLogs = [];
      })
      .addCase(getHvacLogs.fulfilled, (state, action) => {
        state.hvacLogsLoading = false;
        const { success, data } = action.payload || {};
        if (success) state.hvacLogs = data || [];
      })
      .addCase(getHvacLogs.rejected, (state) => {
        state.hvacLogsLoading = false;
      });

    builder
      .addCase(getHvacAreas.pending, (state) => {
        state.areasLoading = true;
      })
      .addCase(getHvacAreas.fulfilled, (state, action) => {
        state.areasLoading = false;
        const { success, data } = action.payload || {};
        if (success && data) state.areas = data.areas || [];
      })
      .addCase(getHvacAreas.rejected, (state) => {
        state.areasLoading = false;
      });

    const wireBulk = (thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.bulkActionLoading = true;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.bulkActionLoading = false;
          state.selectedRowKeys = [];
          const { message: msg } = action.payload || {};
          if (msg) message.success(msg);
        })
        .addCase(thunk.rejected, (state, action) => {
          state.bulkActionLoading = false;
          handleIneligibleError(action);
        });
    };
    wireBulk(bulkSendForCleaning);
    wireBulk(bulkMarkCleanedAndAssign);
    wireBulk(bulkInstallFromSpare);
    wireBulk(bulkInactivateHvac);

    builder
      .addCase(reactivateHvac.pending, (state) => {
        state.bulkActionLoading = true;
      })
      .addCase(reactivateHvac.fulfilled, (state, action) => {
        state.bulkActionLoading = false;
        const { message: msg } = action.payload || {};
        if (msg) message.success(msg);
      })
      .addCase(reactivateHvac.rejected, (state, action) => {
        state.bulkActionLoading = false;
        handleIneligibleError(action);
      });

    builder
      .addCase(generateHvacUsageLogReport.pending, (state) => {
        state.reportGenerating = true;
      })
      .addCase(generateHvacUsageLogReport.fulfilled, (state, action) => {
        state.reportGenerating = false;
        const { success, data, message: msg } = action.payload || {};
        if (success && data?.pdf_url) {
          const filename = String(data.pdf_url)
            .split(/[?#]/)[0]
            .split('/')
            .pop();
          if (filename && process.env.REACT_APP_STORAGE_URL) {
            window.open(
              `${process.env.REACT_APP_STORAGE_URL}${filename}`,
              '_blank'
            );
          } else {
            window.open(data.pdf_url, '_blank');
          }
          if (msg) message.success(msg);
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(generateHvacUsageLogReport.rejected, (state, action) => {
        state.reportGenerating = false;
        handleIneligibleError(action);
      });
  }
});

export const {
  setSelectedRowKeys,
  setActiveTab,
  setAreaSearch,
  clearSelection
} = hvacFilterSlice.actions;

export default hvacFilterSlice.reducer;
